import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api/axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const DynamicPage = () => {
    const { slug } = useParams();
    const [page, setPage] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPage = async () => {
            setIsLoading(true);
            try {
                const res = await api.get(`/custom-pages/slug/${slug}`);
                setPage(res.data);
            } catch (err) {
                console.error("Error fetching dynamic page", err);
                setError(err.response?.status === 404 ? 'Página no encontrada' : 'Error al cargar la página');
            } finally {
                setIsLoading(false);
            }
        };

        fetchPage();
    }, [slug]);

    if (isLoading) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-[#1c0042] to-[#0e0024]">
                <div className="w-12 h-12 border-4 border-white/20 border-t-purple-500 rounded-full animate-spin"></div>
            </div>
        );
    }

    if (error || !page) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-[#1c0042] to-[#0e0024]">
                <div className="bg-white/10 backdrop-blur-md p-10 rounded-3xl text-center shadow-2xl border border-white/10">
                    <h1 className="text-4xl font-black text-white italic uppercase tracking-tighter mb-4">Error 404</h1>
                    <p className="text-white/60 mb-8 uppercase tracking-widest text-xs font-bold">{error}</p>
                    <Link to="/" className="px-8 py-4 bg-white text-[#1c0042] font-black uppercase tracking-widest text-xs rounded-full hover:scale-105 transition-transform">
                        Volver al Inicio
                    </Link>
                </div>
            </div>
        );
    }

    const renderComponent = (comp, index) => {
        const { type, props } = comp;

        switch (type) {
            case 'TextBlock':
                return (
                    <div 
                        key={comp.id} 
                        className="w-full my-4 px-4 sm:px-0 animate-fadeIn"
                        style={{ 
                            color: props.color || '#ffffff', 
                            textAlign: props.align || 'left', 
                            fontSize: props.fontSize || '16px',
                            fontWeight: props.fontWeight === 'black' ? 900 : props.fontWeight === 'bold' ? 700 : 400
                        }}
                        dangerouslySetInnerHTML={{ __html: props.content }}
                    />
                );

            case 'Image':
                return (
                    <div key={comp.id} className="w-full flex justify-center my-6 animate-scaleUp">
                        <img 
                            src={props.url} 
                            alt={props.alt || ''} 
                            style={{ 
                                width: props.width || '100%', 
                                borderRadius: props.borderRadius || '0px',
                                maxWidth: '100%'
                            }} 
                            className="shadow-2xl"
                        />
                    </div>
                );

            case 'Video':
                return (
                    <div key={comp.id} className="w-full relative pt-[56.25%] my-8 rounded-3xl overflow-hidden shadow-2xl border border-white/10 animate-fadeIn">
                        <iframe
                            className="absolute inset-0 w-full h-full"
                            src={props.url}
                            title={props.title || 'Video Embed'}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        ></iframe>
                    </div>
                );

            case 'Button':
                const isExternal = props.link?.startsWith('http');
                const btnStyle = {
                    backgroundColor: props.bgColor || '#4f46e5',
                    color: props.textColor || '#ffffff'
                };
                const btnClass = "inline-block px-10 py-4 rounded-full font-black uppercase tracking-widest text-xs shadow-xl transition-transform hover:scale-105 active:scale-95 text-center";
                
                return (
                    <div key={comp.id} className="w-full flex justify-center my-6 animate-scaleUp">
                        {isExternal ? (
                            <a href={props.link} target="_blank" rel="noreferrer" className={btnClass} style={btnStyle}>
                                {props.text}
                            </a>
                        ) : (
                            <Link to={props.link} className={btnClass} style={btnStyle}>
                                {props.text}
                            </Link>
                        )}
                    </div>
                );

            case 'Spacer':
                return (
                    <div key={comp.id} style={{ height: props.height || '50px', width: '100%' }}></div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#1c0042] to-[#0e0024] pb-32">
            <Header title={page.title} subtitle="Página Personalizada" />
            
            <div className="max-w-4xl mx-auto mt-10 p-6 sm:p-0">
                {page.components && Array.isArray(page.components) ? (
                    page.components.map((comp, idx) => renderComponent(comp, idx))
                ) : null}
            </div>

            <Footer />
        </div>
    );
};

export default DynamicPage;
